import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import { OutlinedTextFieldProps } from '@mui/material/TextField';
import cx from 'classnames';

import { ParameterEditorBaseProps } from 'common/ui/components/ParameterEditorBaseProps';
import getMeasurementFromString from 'common/ui/components/ParameterEditors/unitRegistry';
import TextField from 'common/ui/filaments/TextField';
import makeStylesHook from 'common/ui/hooks/makeStylesHook';
import useTextFieldChange from 'common/ui/hooks/useTextFieldChange';

type AdditionalTextFieldProps = Omit<
  OutlinedTextFieldProps,
  'value' | 'onChange' | 'variant'
>;

type ExtraProps =
  | {
      hideError: true;
      overrideErrorState?: boolean;
    }
  | {
      hideError?: false;
      overrideErrorState?: never;
    };

type Props = {
  units: string[];
  onChange: (value?: string) => void;
  defaultUnit?: string;
  disableUnderline?: boolean;
  className?: string;
  textFieldProps?: AdditionalTextFieldProps;
} & ParameterEditorBaseProps<string> &
  ExtraProps;

const StringMeasurementEditor = React.memo(function StringMeasurementEditor(
  props: Props,
) {
  const classes = useStyles();
  const { onChange, value: strMeasurement, units, defaultUnit = '' } = props;
  const measurement = useMemo(
    () => getMeasurementFromString(strMeasurement ?? defaultUnit, undefined, units),
    [defaultUnit, strMeasurement, units],
  );

  const [currentValue, setCurrentValue] = useState(
    isNaN(measurement.value) ? '' : measurement.value.toString(),
  );
  const [selectedUnit, setSelectedUnit] = useState(measurement.unit);
  const [inputUnit, setInputUnit] = useState(selectedUnit);

  useEffect(() => {
    const hasValue = !isNaN(measurement.value);
    const initialValue = hasValue ? measurement.value.toString() : '';
    setCurrentValue(initialValue);

    if (hasValue && !measurement.unit) {
      setSelectedUnit('');
    } else {
      setSelectedUnit(measurement.unit || defaultUnit || '');
    }
  }, [defaultUnit, measurement.unit, measurement.value]);

  const onBlurValue = useCallback(() => {
    const newValue = formatValue(currentValue, selectedUnit);
    if (newValue === strMeasurement) {
      return;
    }

    onChange(newValue);
  }, [currentValue, onChange, selectedUnit, strMeasurement]);

  const onChangeSelectedUnit = useCallback(
    (_event: any, unit: string | null) => {
      if (unit) {
        setSelectedUnit(unit);
      }
      const newValue = formatValue(currentValue, unit ?? '');
      onChange(newValue);
    },
    [currentValue, onChange],
  );

  const onChangeInputUnit = useCallback(
    (_event: any, unit: string) => setInputUnit(unit),
    [],
  );

  const onChangeValue = useTextFieldChange((value: string) => {
    /**
     * We don't change the props when the user types here, because we
     * would then be running getMeasurementFromString() on every change
     * which validates by default against a large number of units in the
     * unitRegistry. So wait until the user has completed (using onBlurValue)
     * and only run getMeasurementFromString() once.
     * */
    setCurrentValue(value);
  });

  const validationError =
    currentValue !== '' && !/^[0-9.+\-eE\b]+$/.test(currentValue)
      ? 'Only numbers are valid'
      : '';

  const errorState = props.hideError
    ? props.overrideErrorState ?? false
    : validationError !== '';

  const helperText = props.hideError ? null : validationError !== '' && validationError;

  const isSingleUnitOption = units.length === 1 && !!defaultUnit;

  return (
    <div className={cx(classes.box, props.className)}>
      <TextField
        {...props.textFieldProps}
        className={classes.value}
        placeholder={props.placeholder ?? ''}
        InputLabelProps={{
          shrink: true,
        }}
        InputProps={{
          ...props.textFieldProps?.InputProps,
          disableUnderline: props.disableUnderline,
        }}
        inputRef={props.textFieldProps?.inputRef}
        value={currentValue}
        onChange={onChangeValue}
        onBlur={onBlurValue}
        disabled={props.isDisabled}
        required={props.isRequired}
        error={errorState}
        helperText={helperText}
        inputProps={{
          style: { textAlign: 'right' },
          ...props.textFieldProps?.inputProps,
        }}
      />
      <Autocomplete
        blurOnSelect
        disableClearable
        disabled={props.isDisabled || currentValue === ''}
        readOnly={isSingleUnitOption}
        forcePopupIcon={!isSingleUnitOption}
        selectOnFocus={!isSingleUnitOption}
        options={units}
        className={classes.unit}
        value={selectedUnit}
        onChange={onChangeSelectedUnit}
        inputValue={inputUnit}
        onInputChange={onChangeInputUnit}
        renderInput={params => (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              disableUnderline: props.disableUnderline,
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        )}
      />
    </div>
  );
});

const useStyles = makeStylesHook({
  box: {
    display: 'flex',
  },
  unit: {
    flex: 1,
  },
  value: {
    flex: 1.25,
    'margin-right': '10px',
  },
});

function formatValue(value: string, unit: string) {
  const num = parseFloat(value);
  if (isNaN(num)) {
    return '';
  }
  return `${num}${unit ?? ''}`;
}

export default StringMeasurementEditor;
